import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FAQ } from "@/components/FAQ";
import { OptImg } from "@/components/OptImg";
import { CTASection } from "@/components/CTASection";
import { Tilt3D } from "@/components/Tilt3D";
import { ShareButtons } from "@/components/ShareButtons";
import { findPost, type Post } from "@/lib/blog";
import { seoTitle, seoDescription } from "@/lib/seo-meta";
import { formatDateTimeBR } from "@/lib/format";
import { getRenderedPost, listRelatedPosts } from "@/lib/content.functions";
import { extractYouTubeId, extractHeadings } from "@/lib/internal-links";
import { ProductCard } from "@/components/ProductCard";
import { REVIEWS } from "@/lib/reviews";
import { BusinessProfileCard } from "@/components/BusinessBlocks";

function pickReviewsFor(key: string, count = 4) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  const pool = REVIEWS;
  const out: { name: string; text: string; rating: number; location?: string }[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < count && seen.size < pool.length; i++) {
    let idx = (h + i * 7) % pool.length;
    while (seen.has(idx)) idx = (idx + 1) % pool.length;
    seen.add(idx);
    const r = pool[idx];
    out.push({ name: r.name, text: r.text, rating: r.rating, location: r.neighborhood });
  }
  return out;
}

type ProductData = {
  product_name?: string | null;
  product_image?: string | null;
  product_image_alt?: string | null;
  product_url?: string | null;
  product_price?: string | null;
  product_rating?: number | null;
  product_reviews_count?: number | null;
  product_features?: string[] | null;
  product_cta_label?: string | null;
  product_marketplace?: string | null;
  product_cta_bg_color?: string | null;
  product_cta_text_color?: string | null;
};

type BusinessPartner = {
  name?: string | null; description?: string | null; logo?: string | null;
  website?: string | null; whatsapp?: string | null;
};
type BusinessData = {
  nap?: { name?: string; address?: string; phone?: string; hours?: string } | null;
  description?: string | null;
  socials?: { instagram?: string; facebook?: string; linkedin?: string; youtube?: string; website?: string } | null;
  google_maps_embed?: string | null;
  partners?: BusinessPartner[] | null;
} | null;

type LoaderData = Post & {
  html?: string;
  coverImage?: string | null;
  youtubeUrls?: string[];
  product?: ProductData | null;
  comparisonProducts?: ProductData[];
  articleType?: string | null;
  businessData?: BusinessData;
};


export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const staticPost = findPost(params.slug);
    if (staticPost) return staticPost as LoaderData;
    const dbPost = await getRenderedPost({ data: { slug: params.slug } });
    if (!dbPost) throw notFound();
    const mapped: LoaderData = {
      slug: dbPost.slug,
      title: dbPost.title,
      description: dbPost.description,
      category: dbPost.category,
      author: dbPost.author,
      date: dbPost.published_at ?? new Date().toISOString(),
      readingTime: dbPost.reading_time,
      cover: dbPost.cover,
      excerpt: dbPost.excerpt || dbPost.description,
      content: [],
      html: dbPost.html,
      coverImage: dbPost.cover_image ?? null,
      youtubeUrls: dbPost.youtube_urls ?? [],
      product: {
        product_name: (dbPost as any).product_name ?? null,
        product_image: (dbPost as any).product_image ?? null,
        product_image_alt: (dbPost as any).product_image_alt ?? null,
        product_url: (dbPost as any).product_url ?? null,
        product_price: (dbPost as any).product_price ?? null,
        product_rating: (dbPost as any).product_rating ?? null,
        product_reviews_count: (dbPost as any).product_reviews_count ?? null,
        product_features: (dbPost as any).product_features ?? null,
        product_cta_label: (dbPost as any).product_cta_label ?? null,
        product_marketplace: (dbPost as any).product_marketplace ?? null,
        product_cta_bg_color: (dbPost as any).product_cta_bg_color ?? null,
        product_cta_text_color: (dbPost as any).product_cta_text_color ?? null,
      },
      articleType: (dbPost as any).article_type ?? null,
      comparisonProducts: Array.isArray((dbPost as any).comparison_products)
        ? ((dbPost as any).comparison_products as ProductData[])
        : [],
      businessData: ((dbPost as any).business_data ?? null) as BusinessData,
    };
    return mapped;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Artigo | Empresa Brasil Online" }] };
    const url = `https://empresasbrasilonline.com.br/blog/${loaderData.slug}`;
    const absoluteUrl = url;
    const ogImage =
      loaderData.coverImage && loaderData.coverImage.startsWith("http")
        ? loaderData.coverImage
        : loaderData.coverImage
          ? `https://empresasbrasilonline.com.br${loaderData.coverImage.startsWith("/") ? "" : "/"}${loaderData.coverImage}`
          : null;
    const title = seoTitle(`${loaderData.title} | Empresa Brasil Online`);
    const desc = seoDescription(loaderData.description);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(ogImage
          ? [
              { property: "og:image", content: ogImage },
              { property: "og:image:alt", content: loaderData.title },
              { name: "twitter:card", content: "summary_large_image" },
              { name: "twitter:image", content: ogImage },
              { name: "twitter:image:alt", content: loaderData.title },
              { name: "twitter:title", content: title },
              { name: "twitter:description", content: desc },
            ]
          : []),
        { property: "article:published_time", content: loaderData.date },
        { property: "article:author", content: loaderData.author },
        { property: "article:section", content: loaderData.category },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: loaderData.title,
            description: loaderData.description,
            image: ogImage ? [ogImage] : undefined,
            mainEntityOfPage: { "@type": "WebPage", "@id": absoluteUrl },
            author: { "@type": "Organization", name: loaderData.author },
            datePublished: loaderData.date,
            articleSection: loaderData.category,
            publisher: { "@type": "Organization", name: "Empresa Brasil Online" },
          }),
        },
      ],
    };

  },
  component: Post,
  notFoundComponent: () => (
    <Section><p>Artigo não encontrado.</p></Section>
  ),
});

function Post() {
  const post = Route.useLoaderData() as LoaderData;
  const { data: related = [] } = useQuery({
    queryKey: ["related-posts", post.slug, post.category],
    queryFn: () => listRelatedPosts({ data: { slug: post.slug, category: post.category, limit: 4 } }),
    staleTime: 60_000,
  });
  const hasHtml = Boolean(post.html);
  const ytIds = (post.youtubeUrls ?? [])
    .map((u) => extractYouTubeId(u))
    .filter((id): id is string => Boolean(id));
  const headings = hasHtml ? extractHeadings(post.html ?? "") : [];
  const product = post.product ?? null;
  const comparisonProducts = (post.comparisonProducts ?? []).filter((p) => p?.product_name && p?.product_url);
  const hasProduct = Boolean(product?.product_name && product?.product_url);
  // All affiliate products in order (primary + comparison). Index N corresponds to {{PRODUCT_CARD:N}}.
  const allProducts: ProductData[] = hasProduct ? [product as ProductData, ...comparisonProducts] : comparisonProducts;
  const isMultiProduct = allProducts.length > 1;
  const businessData = post.businessData ?? null;
  const hasBusinessProfile = Boolean(businessData && (
    businessData.nap?.name || businessData.nap?.address || businessData.nap?.phone || businessData.nap?.hours
    || businessData.google_maps_embed
    || businessData.socials?.website || businessData.socials?.instagram || businessData.socials?.facebook
    || businessData.socials?.linkedin || businessData.socials?.youtube
  ));
  // Split rendered HTML on PRODUCT_CARD markers only. The empresa profile card
  // is always rendered as a standalone block at the top of the article (see
  // below) instead of being injected mid-body.
  type Slot = { kind: "product"; index: number };
  const TOKEN_RE = /(?:<p>\s*)?\{\{\s*PRODUCT_CARD(?::(\d+))?\s*\}\}(?:\s*<\/p>)?/gi;
  const htmlSegments: string[] = [];
  const slots: Slot[] = [];
  if (hasHtml) {
    const src = post.html ?? "";
    let lastEnd = 0;
    let m: RegExpExecArray | null;
    let fallbackIdx = 0;
    while ((m = TOKEN_RE.exec(src))) {
      htmlSegments.push(src.slice(lastEnd, m.index));
      const n = m[1] ? Number(m[1]) : (isMultiProduct ? fallbackIdx++ : 0);
      slots.push({ kind: "product", index: Number.isFinite(n) ? n : 0 });
      lastEnd = m.index + m[0].length;
    }
    htmlSegments.push(src.slice(lastEnd));
  }
  const htmlHasMarker = slots.length > 0;
  const POST_HTML_CLASS = "post-html space-y-5 text-base leading-relaxed text-muted-foreground [&_h2]:mt-10 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground md:[&_h2]:text-3xl [&_h3]:mt-8 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-foreground [&_p]:mt-4 [&_ul]:mt-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mt-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_strong]:text-foreground [&_a]:text-primary [&_a:hover]:underline [&_figure]:my-6 [&_table]:my-6 [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-xl [&_table]:border [&_table]:border-border [&_thead]:bg-secondary [&_th]:border [&_th]:border-border [&_th]:p-3 [&_th]:text-left [&_th]:text-sm [&_th]:font-semibold [&_th]:text-foreground [&_td]:border [&_td]:border-border [&_td]:p-3 [&_td]:text-sm [&_img]:my-6 [&_img]:rounded-xl [&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:bg-secondary/40 [&_blockquote]:p-4 [&_blockquote]:italic [&_hr]:my-8 [&_hr]:border-border";
  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-10 pb-10 md:pt-16 md:pb-12">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Blog", to: "/blog" }, { label: post.title }]} />
          <div className="mt-6 max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">{post.category}</span>
            <h1 className="mt-3 text-3xl font-bold tracking-tight md:text-5xl">{post.title}</h1>
            <p className="mt-4 text-muted-foreground md:text-lg">{post.description}</p>
            <div className="mt-5 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>{post.author}</span>
              <span>{formatDateTimeBR(post.date)}</span>
              <span>{post.readingTime} de leitura</span>
            </div>
          </div>
          {post.coverImage ? (
            <OptImg
              src={post.coverImage}
              alt={post.title}
              priority
              width={1200}
              height={480}
              widths={[480, 768, 1024, 1280, 1600]}
              sizes="(max-width: 768px) 100vw, 1200px"
              className="mt-8 h-56 w-full rounded-2xl object-cover md:h-80"
            />
          ) : (
            <div className="mt-8 h-40 rounded-2xl md:h-56" style={{ background: post.cover }} aria-hidden />
          )}
        </div>
      </section>

      <Section>
        <div className="grid gap-12 lg:grid-cols-[260px_1fr]">
          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start order-2 lg:order-1">
            {headings.length > 0 && (
              <div className="card-glass hidden lg:block">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sumário</p>
                <nav className="mt-3 max-h-[60vh] space-y-1 overflow-y-auto pr-1 text-sm" aria-label="Sumário do artigo">
                  {headings.map((h) => (
                    <a
                      key={h.id}
                      href={`#${h.id}`}
                      className={`block transition hover:text-primary ${h.level === 3 ? "pl-3 text-xs text-muted-foreground" : "text-foreground"}`}
                    >
                      {h.text}
                    </a>
                  ))}
                </nav>
              </div>
            )}
            <div className="card-glass">
              <p className="text-sm font-semibold">Categoria</p>
              <p className="mt-2 text-xs text-primary">{post.category}</p>
            </div>
            <ShareButtons title={post.title} slug={post.slug} placement="sidebar" />
          </aside>

          <article className="prose-svg order-1 lg:order-2">
            {hasHtml ? (
              <div className="mb-10">
                {hasBusinessProfile && (
                  <BusinessProfileCard
                    nap={businessData?.nap ?? null}
                    socials={businessData?.socials ?? null}
                    googleMapsEmbed={businessData?.google_maps_embed ?? null}
                  />
                )}
                {headings.length > 0 && (
                  <nav className="mb-8 rounded-xl border border-border bg-secondary/40 p-5 lg:hidden" aria-label="Sumário">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sumário</p>
                    <ol className="mt-2 space-y-1 text-sm">
                      {headings.map((h) => (
                        <li key={h.id} className={h.level === 3 ? "pl-4" : ""}>
                          <a href={`#${h.id}`} className="text-foreground hover:text-primary">{h.text}</a>
                        </li>
                      ))}
                    </ol>
                  </nav>
                )}
                {hasProduct && product && (post.articleType === "review" || post.articleType === "comparativo" || post.articleType === "melhores") && (
                  <aside className="not-prose mb-10 overflow-hidden rounded-3xl border border-primary/40 bg-gradient-to-br from-primary/15 via-background to-background p-6 md:p-8 shadow-[var(--shadow-glow)]">
                    <div className="grid gap-6 md:grid-cols-[1fr_220px] md:items-center">
                      <div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">Produto em destaque</span>
                        <h2 className="mt-2 text-2xl font-bold leading-tight text-foreground md:text-3xl">{product.product_name}</h2>
                        {product.product_marketplace && (
                          <p className="mt-1 text-xs text-muted-foreground">disponível na {product.product_marketplace}</p>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                          {typeof product.product_rating === "number" && product.product_rating > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 font-semibold">
                              ★ {product.product_rating.toFixed(1).replace(".", ",")}/5
                            </span>
                          )}
                          {typeof product.product_reviews_count === "number" && product.product_reviews_count > 0 && (
                            <span className="text-muted-foreground">{product.product_reviews_count.toLocaleString("pt-BR")} avaliações</span>
                          )}
                          {product.product_price && (
                            <span className="text-muted-foreground">A partir de <strong className="text-foreground">{product.product_price}</strong></span>
                          )}
                        </div>
                        {(product.product_features ?? []).length > 0 && (
                          <ul className="mt-4 grid gap-1.5 text-sm text-muted-foreground sm:grid-cols-2">
                            {(product.product_features ?? []).slice(0, 6).map((f) => (
                              <li key={f} className="flex items-start gap-2">
                                <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-5">
                          <a
                            href={product.product_url!}
                            target="_blank"
                            rel="sponsored nofollow noopener"
                            aria-label={`${product.product_cta_label || "Ver oferta"} — ${product.product_name} (link de afiliado)`}
                            style={{
                              background: `linear-gradient(135deg, ${product.product_cta_bg_color || "#FF6A00"}, color-mix(in oklab, ${product.product_cta_bg_color || "#FF6A00"} 70%, #000))`,
                              color: product.product_cta_text_color || "#FFFFFF",
                              boxShadow: `0 14px 30px -10px color-mix(in oklab, ${product.product_cta_bg_color || "#FF6A00"} 60%, transparent)`,
                            }}
                            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-extrabold uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5 hover:scale-[1.02]"
                          >
                            {product.product_cta_label || (product.product_marketplace ? `Comprar na ${product.product_marketplace}` : "Ver oferta agora")}
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></svg>
                          </a>
                          <p className="mt-2 text-[11px] text-muted-foreground">🔒 Link de afiliado — você nos apoia sem custo extra.</p>
                        </div>
                      </div>
                      {product.product_image && (
                        <div className="overflow-hidden rounded-2xl bg-secondary/40 order-first md:order-last">
                          <img
                            src={product.product_image}
                            alt={product.product_image_alt || product.product_name || "Produto"}
                            loading="eager"
                            className="aspect-square w-full object-contain p-3"
                          />
                        </div>
                      )}
                    </div>
                  </aside>
                )}
                {htmlSegments.length > 0 && htmlHasMarker ? (
                  <>
                    {htmlSegments.map((seg, i) => {
                      const slot = i < slots.length ? slots[i] : null;
                      const cardProduct = slot && slot.kind === "product"
                        ? (allProducts[slot.index] ?? allProducts[0] ?? null)
                        : null;
                      return (
                        <div key={i}>
                          <div className={POST_HTML_CLASS} dangerouslySetInnerHTML={{ __html: seg }} />
                          {slot?.kind === "product" && cardProduct && cardProduct.product_name && cardProduct.product_url && (
                            <ProductCard
                              name={cardProduct.product_name}
                              image={cardProduct.product_image ?? undefined}
                              imageAlt={cardProduct.product_image_alt ?? undefined}
                              url={cardProduct.product_url}
                              price={cardProduct.product_price ?? undefined}
                              rating={cardProduct.product_rating ?? undefined}
                              reviewsCount={cardProduct.product_reviews_count ?? undefined}
                              features={cardProduct.product_features ?? undefined}
                              ctaLabel={cardProduct.product_cta_label ?? undefined}
                              ctaBgColor={(cardProduct as any).product_cta_bg_color ?? undefined}
                              ctaTextColor={(cardProduct as any).product_cta_text_color ?? undefined}
                              marketplace={cardProduct.product_marketplace ?? undefined}
                              reviews={pickReviewsFor(`${post.slug}-${cardProduct.product_name}-${i}`)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div
                      className={POST_HTML_CLASS}
                      dangerouslySetInnerHTML={{ __html: post.html ?? "" }}
                    />
                    {allProducts.map((p, i) => (
                      <ProductCard
                        key={i}
                        name={p.product_name!}
                        image={p.product_image ?? undefined}
                        imageAlt={p.product_image_alt ?? undefined}
                        url={p.product_url!}
                        price={p.product_price ?? undefined}
                        rating={p.product_rating ?? undefined}
                        reviewsCount={p.product_reviews_count ?? undefined}
                        features={p.product_features ?? undefined}
                        ctaLabel={p.product_cta_label ?? undefined}
                        ctaBgColor={(p as any).product_cta_bg_color ?? undefined}
                        ctaTextColor={(p as any).product_cta_text_color ?? undefined}
                        marketplace={p.product_marketplace ?? undefined}
                        reviews={pickReviewsFor(`${post.slug}-${p.product_name}-${i}`)}
                      />
                    ))}
                  </>
                )}


              </div>
            ) : (
              <>
                <nav className="mb-8 rounded-xl border border-border bg-secondary/40 p-5" aria-label="Sumário">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sumário</p>
                  <ol className="mt-2 space-y-1 text-sm">
                    {post.content.map((c, i) => (
                      <li key={i}><a href={`#s-${i}`} className="text-foreground hover:text-primary">{i + 1}. {c.heading}</a></li>
                    ))}
                  </ol>
                </nav>
                {post.content.map((c, i) => (
                  <section key={i} id={`s-${i}`} className="mb-10">
                      <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{c.heading}</h2>
                      <p className="mt-4 text-base leading-relaxed text-muted-foreground">{c.body}</p>
                  </section>
                ))}
              </>
            )}
            {ytIds.length > 0 && (
              <div className="my-10 space-y-6">
                <h2 className="text-2xl font-bold tracking-tight">Veja também</h2>
                {ytIds.map((id) => (
                  <div key={id} className="aspect-video overflow-hidden rounded-2xl border border-border">
                    <iframe
                      src={`https://www.youtube.com/embed/${id}`}
                      title="YouTube video"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="h-full w-full"
                    />
                  </div>
                ))}
              </div>
            )}
            <ShareButtons title={post.title} slug={post.slug} placement="mobile" />
            <div className="my-10 rounded-2xl border border-primary/30 bg-primary/5 p-6">
              <p className="font-semibold">Quer ler mais artigos da Empresa Brasil Online?</p>
              <p className="mt-1 text-sm text-muted-foreground">Explore as categorias: gestão, finanças, marketing, vendas, jurídico, RH e franquias.</p>
              <Link to="/blog" className="btn-primary mt-4">Conhecer mais conteúdos</Link>
            </div>

            {post.faq && <FAQ items={post.faq} defaultOpen />}
          </article>
        </div>
      </Section>

      <Section className="bg-[color:var(--color-sidebar)]">
        <h2 className="text-2xl font-bold md:text-3xl">Artigos relacionados</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((p, idx) => (
            <Tilt3D key={p.slug}>
                <Link to="/blog/$slug" params={{ slug: p.slug }} className="card-glass block">
                  {p.cover_image ? (
                    <OptImg src={p.cover_image} alt={p.title}
                      width={400} height={160}
                      widths={[240, 320, 480, 640]}
                      sizes="(max-width: 768px) 92vw, 25vw"
                      className="mb-4 h-28 w-full rounded-xl object-cover" />
                  ) : (
                    <div className="mb-4 h-28 rounded-xl" style={{ background: p.cover }} />
                  )}
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">{p.category}</span>
                  <h3 className="mt-2 text-base font-semibold">{p.title}</h3>
                </Link>
            </Tilt3D>
          ))}
          {related.length === 0 && (
            <p className="text-muted-foreground">Em breve mais artigos relacionados.</p>
          )}
        </div>
      </Section>

      <CTASection />
    </>
  );
}